import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, FileSpreadsheet, Presentation as Slides, GitBranch, Loader2, Check, AlertCircle } from 'lucide-react';
import { CognaraLogo } from './CognaraLogo';

/**
 * DocGenProgressCard — live progress surface for the async /documents/generate job.
 *
 * Renders inline in the chat thread while the background job runs. Shows:
 *   • A stage label that follows the server's reported status when available
 *     (starting → pending → drafting → building → ready), and falls back to a
 *     time-based rotation while in the catch-all `generating` state.
 *   • An elapsed timer in mono so users see real-time feedback
 *   • A determinate-looking segmented progress bar
 *   • A success / failure resolution card
 *
 * Props:
 *   status   — 'starting' | 'pending' | 'drafting' | 'building' | 'generating' | 'ready' | 'failed'
 *   format   — { id, label, color? }
 *   startedAt — epoch ms when the job was kicked off
 *   error    — string, only when status === 'failed'
 */

const FORMAT_ICON = {
  docx: FileText,
  xlsx: FileSpreadsheet,
  pptx: Slides,
  flowchart: GitBranch,
  mindmap: GitBranch,
  sequence: GitBranch,
  infographic: FileText,
};

// Time-based stages — used as a fallback when the server hasn't reported a
// specific stage yet (i.e. status is generic 'generating' / 'starting').
const STAGES = [
  { from: 0,     label: 'Starting generation…',          progress: 8 },
  { from: 3000,  label: 'Cognara drafting content…',   progress: 22 },
  { from: 15000, label: 'Structuring sections…',         progress: 44 },
  { from: 35000, label: 'Formatting output…',            progress: 68 },
  { from: 60000, label: 'Finalizing document…',          progress: 86 },
  { from: 95000, label: 'Almost there…',                 progress: 94 },
];

// Server-status → user-visible stage. Used when the server explicitly reports
// one of the new fine-grained stages (`drafting`, `building`). For the generic
// `pending` / `generating` / `starting` states we fall back to a time-based
// rotation so the user still sees forward motion when the backend doesn't
// emit intermediate stages (e.g. older deployments).
const SERVER_STAGE = {
  drafting:  { label: 'Cognara Engine drafting content…', progress: 40 },
  building:  { label: 'Building the file…',               progress: 78 },
};

// Statuses for which the timer + stage animation should keep running.
const IN_FLIGHT_STATUSES = new Set([
  'starting', 'pending', 'drafting', 'building', 'generating',
]);

export const DocGenProgressCard = ({ status = 'generating', format, startedAt, error, progressLabel, progressRatio, targetScale, events }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!IN_FLIGHT_STATUSES.has(status)) return;
    const tick = () => setElapsed(Date.now() - (startedAt || Date.now()));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [status, startedAt]);

  // Prefer the server-reported stage when known; otherwise fall back to the
  // time-based rotation so the card still feels alive on legacy backends.
  const serverStage = SERVER_STAGE[status];
  const timeStage = [...STAGES].reverse().find(s => elapsed >= s.from) || STAGES[0];
  // If the server is sending a live progressLabel (e.g. "Materializing rows:
  // 42,000 / 100,000"), use it verbatim — that's the most accurate stage info
  // we have. Numeric ratio also wins over the simulated progress %.
  const stage = progressLabel
    ? { label: progressLabel, progress: typeof progressRatio === 'number' ? Math.round(progressRatio * 100) : (serverStage || timeStage).progress }
    : (serverStage || timeStage);
  const Icon = FORMAT_ICON[format?.id] || FileText;
  const colorHex = format?.color || '#6BA6F5';

  const isDone = status === 'ready';
  const isFailed = status === 'failed';
  const progressPct = isDone ? 100 : (isFailed ? 100 : stage.progress);
  const seconds = Math.floor(elapsed / 1000);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25 }}
      className="flex gap-3"
      data-testid="docgen-progress-card"
    >
      {/* Avatar — matches assistant bubble styling */}
      <div
        className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-nexus-accent-mint to-nexus-accent-blue flex items-center justify-center mt-1"
        aria-hidden="true"
      >
        <CognaraLogo variant="icon" size={18} style={{ color: '#FFFFFF' }} />
      </div>

      <div
        className="flex-1 max-w-[85%] rounded-2xl rounded-tl-sm border p-4 backdrop-blur-sm"
        style={{
          background: 'color-mix(in srgb, var(--layer-2) 88%, transparent)',
          borderColor: isFailed ? 'rgba(220,80,80,0.35)' : `${colorHex}40`,
        }}
      >
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: `${colorHex}1A`, border: `1px solid ${colorHex}33` }}
          >
            {isDone ? (
              <Check size={16} style={{ color: colorHex }} />
            ) : isFailed ? (
              <AlertCircle size={16} className="text-red-400" />
            ) : (
              <Icon size={16} style={{ color: colorHex }} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <div className="ig-eyebrow text-[10px]" style={{ color: colorHex }}>
                {format?.label || 'Document'} · generation
              </div>
              <div className="ig-mono text-[11px] text-nexus-text-tertiary tabular-nums">
                {seconds}s
              </div>
            </div>
            <div className="mt-0.5 text-sm font-medium text-nexus-text-primary flex items-center gap-2 min-w-0">
              {!isDone && !isFailed && (
                <Loader2 size={13} className="flex-shrink-0 animate-spin" style={{ color: colorHex }} />
              )}
              <AnimatePresence mode="wait">
                <motion.span
                  key={isDone ? 'done' : isFailed ? 'failed' : stage.label}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="truncate"
                >
                  {isDone
                    ? `${format?.label || 'Document'} ready — download below`
                    : isFailed
                      ? (error || 'Generation failed')
                      : stage.label}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mt-3 h-1 rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.06)' }}
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="h-full"
            style={{
              background: isFailed
                ? 'linear-gradient(90deg, rgba(220,80,80,0.9), rgba(220,80,80,0.5))'
                : `linear-gradient(90deg, ${colorHex}, ${colorHex}99)`,
            }}
          />
        </div>

        {/* Live activity log — exposes what the engine is actually doing */}
        {!isFailed && Array.isArray(events) && events.length > 0 && (
          <ActivityLog events={events} colorHex={colorHex} done={isDone} />
        )}

        {!isDone && !isFailed && (!Array.isArray(events) || events.length === 0) && (
          <div className="mt-2 text-[11px] text-nexus-text-tertiary">
            Generation runs in the background — feel free to keep reading. Typical time: 30-90 seconds.
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ---- Live activity log -----------------------------------------------------
// Renders a fading list of the most-recent server events in mono. Tail-follow
// stays anchored to the latest entry; older lines dim out. An "Expand" toggle
// reveals the full scrollback so users can audit exactly what the engine did.
const ActivityLog = ({ events, colorHex, done }) => {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  const visibleEvents = expanded ? events : events.slice(-6);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, expanded]);

  if (!events || events.length === 0) return null;
  return (
    <div className="mt-3" data-testid="docgen-activity-log">
      <div className="flex items-baseline justify-between">
        <div className="ig-eyebrow text-[9px] text-nexus-text-tertiary">
          Live activity · {events.length} event{events.length === 1 ? '' : 's'}
        </div>
        {events.length > 6 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-[10px] text-nexus-text-tertiary hover:text-nexus-text-secondary transition-colors"
            data-testid="activity-log-toggle"
          >
            {expanded ? 'Collapse ▲' : `Expand all (${events.length}) ▼`}
          </button>
        )}
      </div>
      <div
        ref={scrollRef}
        className="mt-1.5 rounded-lg overflow-y-auto p-2 ig-mono text-[10.5px] leading-[1.55]"
        style={{
          background: 'rgba(0,0,0,0.22)',
          border: '1px solid rgba(255,255,255,0.04)',
          maxHeight: expanded ? 260 : 130,
          fontFamily: '"JetBrains Mono", "SF Mono", ui-monospace, monospace',
        }}
      >
        {visibleEvents.map((ev, idx) => {
          const isLatest = idx === visibleEvents.length - 1;
          // Fade older lines so the eye lands on the newest
          const opacity = expanded ? 1 : 0.35 + (0.65 * (idx + 1)) / visibleEvents.length;
          return (
            <div
              key={`${ev.ts}-${idx}`}
              className="flex gap-2 items-baseline"
              style={{ opacity, color: isLatest && !done ? colorHex : 'var(--nexus-text-secondary)' }}
            >
              <span className="text-nexus-text-tertiary shrink-0">{(ev.ts || '').slice(11, 19)}</span>
              <span className="shrink-0" style={{ color: isLatest && !done ? colorHex : 'var(--nexus-text-tertiary)' }}>›</span>
              <span className="truncate">{ev.label}</span>
              {ev.detail ? (
                <span className="text-nexus-text-tertiary truncate">· {ev.detail}</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocGenProgressCard;
